import {
  Box,
  BoxProps,
  Heading,
  ListItem,
  OrderedList,
  Table,
  Td,
  Text,
  Th,
  UnorderedList,
} from "@chakra-ui/react";
import React from "react";
import type { Components } from "react-markdown";

import { PrettyMarkdown } from "../../../../components";

type CustomInsightsReportPrettyMarkdownProps = BoxProps & {
  children: string;
};

const customInsightsReportMarkdownComponents: Partial<Components> = {
  h1: ({ children }) => (
    <Heading
      as="h1"
      fontSize="24px"
      fontWeight="600"
      lineHeight="32px"
      mt={0}
      mb={2}
      color="gray.800"
    >
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading
      as="h2"
      fontSize="20px"
      fontWeight="600"
      lineHeight="24px"
      mt={6}
      mb={2}
      color="gray.800"
    >
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading
      as="h3"
      fontSize="16px"
      fontWeight="600"
      lineHeight="24px"
      mt={4}
      mb={1}
      color="gray.800"
    >
      {children}
    </Heading>
  ),
  p: ({ children }) => (
    <Text mb={2} fontSize="16px" lineHeight="24px" color="gray.800">
      {children}
    </Text>
  ),
  ul: ({ children }) => (
    <UnorderedList
      mb={2}
      pl={5}
      spacing={1}
      fontSize="16px"
      lineHeight="24px"
      color="gray.800"
    >
      {children}
    </UnorderedList>
  ),
  ol: ({ children }) => (
    <OrderedList
      mb={2}
      pl={5}
      spacing={1}
      fontSize="16px"
      lineHeight="24px"
      color="gray.800"
    >
      {children}
    </OrderedList>
  ),
  li: ({ children }) => (
    <ListItem mb={0} fontSize="16px" lineHeight="24px">
      {children}
    </ListItem>
  ),
  table: ({ children }) => (
    <Box overflowX="auto" mt={6} borderTop="1px solid" borderColor="gray.200">
      <Table size="sm" variant="unstyled">
        {children}
      </Table>
    </Box>
  ),
  th: ({ children }) => (
    <Th
      bg="gray.50"
      color="gray.600"
      fontSize="11.67px"
      fontWeight="500"
      lineHeight="16px"
      textTransform="none"
      letterSpacing="normal"
      py={2}
      h="36px"
      borderBottomWidth="1px"
      borderColor="gray.200"
    >
      {children}
    </Th>
  ),
  td: ({ children }) => (
    <Td
      fontSize="14px"
      lineHeight="20px"
      color="gray.800"
      py={3}
      h="48px"
      borderBottomWidth="1px"
      borderColor="gray.100"
    >
      {children}
    </Td>
  ),
};

export const CustomInsightsReportPrettyMarkdown: React.FC<
  CustomInsightsReportPrettyMarkdownProps
> = ({ children, ...boxProps }) => (
  <PrettyMarkdown
    components={customInsightsReportMarkdownComponents}
    {...boxProps}
  >
    {children}
  </PrettyMarkdown>
);
